import React, { useState } from 'react'
import styled from 'styled-components'
import CustomView from '../CustomView'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import { Checkbox, SxProps, Theme } from '@mui/material'
import { useTableData } from '@/hooks/useTableData'
import { useSearchParams } from '@/hooks/useSearchParams'
import { Data } from '@/types/customTable.types'

interface ITableHead {
  dataset: Data[]
  columns: string[]
  hasAction: boolean
  showSelectColumn?: boolean
  canChoiceOnlyOneSelect?: boolean
  enableSort?: boolean
  disableSearchParams?: boolean
  selectionMatchFields?: string[]
}

const headerCellSx: SxProps<Theme> = {
  minWidth: '140px',
  maxWidth: '180px',
  color: '#666766',
  flexDirection: 'row',
  fontSize: '15px',
  py: '10px',
  ml: '8px',
  display: 'flex',
  textAlign: 'center',
  cursor: 'pointer',
  alignItems: 'center',
  justifyContent: 'center',
  '&:hover': {
    backgroundColor: '#d0d4dd',
    color: '#142954'
  },
  '&:first-of-type': {
    justifyContent: 'center'
  },
  '@media screen and (max-width: 769px)': {
    justifyContent: 'center',
    alignItems: 'center'
  }
}

const ArrowUpwardIconCustom = styled(ArrowUpwardIcon)`
  &.ascending {
    transform: rotate(180deg);
  }
`

function TableHead({
  dataset,
  columns,
  hasAction,
  showSelectColumn,
  selectionMatchFields,
  canChoiceOnlyOneSelect,
  enableSort = true,
  disableSearchParams = false
}: ITableHead) {
  const {
    CHECK_SELECTS,
    SET_CHECK_SELECTS,
    SET_SORT_FIELD,
    SET_SORT_ASCENDING,
    SORT_ASCENDING
  } = useTableData()
  const [columnClick, setColumnClick] = useState<number>()
  const { includeParamsToUrl } = useSearchParams()

  function clickColumn(indexItem: number) {
    if (!dataset || !enableSort) return

    const dataKeys = Object.keys(dataset[0]).filter((key) => key !== 'id')
    const sortField = dataKeys[indexItem]
    SET_SORT_FIELD(sortField)

    const sortAscending = columnClick === indexItem ? !SORT_ASCENDING : true
    SET_SORT_ASCENDING(sortAscending)
    setColumnClick(indexItem)

    if (!disableSearchParams) includeParamsToUrl({ sortField, sortAscending })
  }

  function handleSelects(e: any) {
    if (!e.target.checked) {
      const allNoChecked = CHECK_SELECTS.map(() => ({
        checked: false,
        id: '',
        row: null
      }))
      SET_CHECK_SELECTS([...allNoChecked])
      return
    }

    if (!selectionMatchFields?.length) {
      const allChecked = CHECK_SELECTS.map((_: any, index: number) => ({
        checked: true,
        id: dataset[index]?.id
      })).filter((item: any) => item.id?.length)
      SET_CHECK_SELECTS([...allChecked])
      return
    }

    //prettier-ignore
    const selectedItem = selectionMatchFields.length > 0
        ? CHECK_SELECTS.find((value: any) => value.checked)
        : null

    const referenceRow = selectedItem?.row ?? dataset[0]

    const allChecked = dataset.map((row: any) => {
      const matchesSelection = selectionMatchFields?.every(
        (field) => row[field] === referenceRow[field]
      )

      if (!matchesSelection || !row.id) {
        return { checked: false, id: '', row: null }
      }

      return { checked: true, id: row.id, row }
    })

    SET_CHECK_SELECTS([...allChecked])
  }

  function handleAreAllSelectable() {
    if (!selectionMatchFields?.length) {
      return CHECK_SELECTS.every((value: any) => value.checked === true)
    }

    const selectedItem = CHECK_SELECTS.find((value: any) => value.checked)
    const referenceRow = selectedItem?.row

    if (!referenceRow) return false

    const selectableRows = dataset.filter((row: any) =>
      selectionMatchFields.every((field) => row[field] === referenceRow[field])
    )

    return (
      selectableRows.length > 0 &&
      selectableRows.every((row) =>
        CHECK_SELECTS.some((value: any) => value.checked && value.id === row.id)
      )
    )
  }

  return (
    <CustomView
      flexDirection="row"
      backColor="white"
      gap={1}
      position="sticky"
      top={0}
      zIndex={999}
    >
      {showSelectColumn && (
        <Checkbox
          sx={{ marginLeft: '24px' }}
          onChange={handleSelects}
          disabled={canChoiceOnlyOneSelect}
          checked={handleAreAllSelectable()}
        />
      )}

      {hasAction && <CustomView sx={headerCellSx}>Ações</CustomView>}

      {columns.map((column, index) => (
        <React.Fragment key={index}>
          {column && (
            <CustomView
              key={index}
              sx={headerCellSx}
              onClick={() => clickColumn(index)}
            >
              {column}
              {columnClick === index && (
                <ArrowUpwardIconCustom
                  fontSize="medium"
                  className={SORT_ASCENDING ? 'ascending' : ''}
                />
              )}
            </CustomView>
          )}
        </React.Fragment>
      ))}
    </CustomView>
  )
}

export default TableHead
